import React from "react";
import './listing.css';
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import axios from "axios";
import useTour from "../../tour/useTour";
import listingSteps from "../../tour/steps/listingSteps";
import TourButton from "../../components/tourButton/TourButton";
import usePullToRefresh from "../../hooks/usePullToRefresh";

const Listing = ( { isLoggedIn, loginUser, onLogout } ) => {
  const { profileId } = useParams();
  //const [formData, setFormData] = useState({departure: "", arrival: "", estimatedDate: "", totalSeats: "" });
  const [offeredRides, setOfferedRides] = useState([]);
  const [bookedRides, setBookedRides] = useState([]);
  const [recurringRides, setRecurringRides] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [isBookingMode, setIsBookingMode] = useState(false);
  const [offeredRideError, setOfferedRideError] = useState("");
  const [bookedRideError, setBookedRideError] = useState("");
  const [profileName, setProfileName] = useState("");
  const navigate = useNavigate();
  const isOwnProfile = !profileId || profileId === loginUser._id;

  useEffect(() => {
    if (!isOwnProfile) {
      axios.get(`/api/v1/user/${profileId}`)
        .then((res) => setProfileName(res.data.user.userName.split(" ")[0]))
        .catch(() => setProfileName("User"));
    }
  }, [profileId, isOwnProfile]);

  const getOfferedRide = useCallback(async () => {
    try {
      let response;
      if (profileId && profileId !== loginUser._id) {
        response = await axios.get(`/api/v1/ride/${profileId}`);
      } else {
        response = await axios.get(`/api/v1/ride/own`);
      }

      // Use optional chaining and provide fallback values
      const temp_rides = response.data.rides || [];

      const sorted = [...temp_rides].sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
        return new Date(b.estimatedDate) - new Date(a.estimatedDate);
      });
      setOfferedRides(sorted);

      // Clear the error if there are rides
      if (temp_rides.length > 0) {
        setOfferedRideError(null);
      } else {
        setOfferedRideError('⚠️ No rides posted yet.');
      }
    } catch (error) {
        if (error.response && error.response.status === 401) {
          onLogout();
          navigate('/login');
        } else if (error.response) {
          setOfferedRideError(error.response.data.message);
        } else {
          setOfferedRideError('⚠️ An error occurred while fetching rides');
        }
    } finally {
      setLoading(false);
    }
  }, [profileId, loginUser, onLogout, navigate]);

  const getBookedRide = useCallback(async () => {
    try {
      let response;
      if (profileId && profileId !== loginUser._id) {
        response = await axios.get(`/api/v1/ride/bookedRides/${profileId}`);
      } else {
        response = await axios.get(`/api/v1/ride/bookedRides/${loginUser._id}`);
      }

      // Use optional chaining and provide fallback values
      const temp_rides = response.data.bookedRides || [];

      const sorted = [...temp_rides].sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
        return new Date(b.estimatedDate) - new Date(a.estimatedDate);
      });
      setBookedRides(sorted);

      // Clear the error if there are rides
      if (temp_rides.length > 0) {
        setBookedRideError(null);
      } else {
        setBookedRideError('⚠️ No rides booked yet.');
      }
    } catch (error) {
        if (error.response && error.response.status === 401) {
          onLogout();
          navigate('/login');
        } else if (error.response) {
          setBookedRideError(error.response.data.message);
        } else {
          setBookedRideError('⚠️ An error occurred while fetching bookings');
        }
    } finally {
      setLoading(false);
    }
  }, [profileId, loginUser, onLogout, navigate]);

  // Recurring templates are private to the owner, so only fetch on own profile.
  const getRecurringRides = useCallback(async () => {
    if (!isOwnProfile) return;
    try {
      const res = await axios.get(`/api/v1/recurring/my`);
      setRecurringRides(res.data.recurringRides || []);
    } catch (error) {
      // Non-critical — leave the section empty if it fails.
      setRecurringRides([]);
    }
  }, [isOwnProfile]);

  const handleToggleRecurring = async (id) => {
    try {
      await axios.post(`/api/v1/recurring/toggle/${id}?_method=PUT`);
      getRecurringRides();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      }
    }
  };

  const handleDeleteRecurring = async (id) => {
    try {
      await axios.post(`/api/v1/recurring/delete/${id}?_method=PUT`);
      setRecurringRides((prev) => prev.filter((r) => r._id !== id));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      }
    }
  };

  const describeSchedule = (schedule) => {
    if (!schedule) return "";
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const time = schedule.departureTime || "";
    if (schedule.type === "daily") return `Every day · ${time}`;
    if (schedule.type === "weekdays") return `Weekdays · ${time}`;
    const days = (schedule.daysOfWeek || []).slice().sort((a, b) => a - b).map((d) => dayNames[d]).join(", ");
    return `${days || "No days"} · ${time}`;
  };

  const handleRefresh = useCallback(async () => {
    await Promise.all([getOfferedRide(), getBookedRide(), getRecurringRides()]);
  }, [getOfferedRide, getBookedRide, getRecurringRides]);

  const { isRefreshing, pullDistance, containerRef } = usePullToRefresh(handleRefresh);

  const { startTour } = useTour("listing", listingSteps, !loading);

  useEffect(() => {
    getOfferedRide();
  }, [getOfferedRide]);

  useEffect(() => {
    getBookedRide();
  }, [getBookedRide]);

  useEffect(() => {
    getRecurringRides();
  }, [getRecurringRides]);

  const handleBookingClick = () => {
    setIsBookingMode(true); // Set the state to true when the user clicks Sign Up
  };

  const handleOfferedClick = () => {
    setIsBookingMode(false); // Set the state to false when the user clicks Login
  };
  
  if (!isLoggedIn) {
    navigate('/');
    return;
  }

  // Show skeleton while data is being fetched
  if (loading) {
    return (
      <div className="sr-listing">
        <div className="sr-listing__top">
          <div className="sr-listing__top-content">
            <h1 className="sr-listing__title">{isOwnProfile ? "My Rides" : `${profileName || "User"}'s Rides`}</h1>
            <p className="sr-listing__subtitle">{isOwnProfile ? "Your offered and booked rides" : "Offered and booked rides"}</p>
          </div>
        </div>
        <div className="sr-listing__body">
          <div className="sr-listing__toggle">
            <div className="sr-listing__toggle-label sr-listing__toggle-label--active">Offered</div>
            <div className="sr-listing__toggle-label">Booked</div>
            <div className="sr-listing__toggle-slider" />
          </div>
          <div className="sr-listing__rides">
            {[1, 2, 3, 4].map((i) => (
              <div className="sr-listing__card sr-listing__card--skeleton" key={i}>
                <div className="sr-listing__card-dots">
                  <span className="sr-listing__card-dot sr-listing__card-dot--from" />
                  <span className="sr-listing__card-connector" />
                  <span className="sr-listing__card-dot sr-listing__card-dot--to" />
                </div>
                <div className="sr-listing__card-route">
                  <div className="sr-listing__card-cities">
                    <div className="sr-listing__skeleton sr-listing__skeleton--city" />
                    <div className="sr-listing__skeleton sr-listing__skeleton--city sr-listing__skeleton--city-short" />
                  </div>
                  <div className="sr-listing__card-meta">
                    <div className="sr-listing__skeleton sr-listing__skeleton--meta" />
                    <div className="sr-listing__skeleton sr-listing__skeleton--meta sr-listing__skeleton--meta-short" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const handleGetSingleRide = (rideId) => {
    //console.log(`handleGetSingleRide triggered with rideId: ${rideId}`);
    
    navigate(`/ride/${rideId}`);
  };

  const formatRideDate = (estimatedDate) => {
    const date = new Date(estimatedDate);
    const oneDayInMs = 24 * 60 * 60 * 1000;
    if (Math.abs(date - Date.now()) < oneDayInMs) {
      return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', timeZone: 'UTC'});
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const renderRideCard = (element) => {
    const { _id, arrival, departure, estimatedDate, route, isCompleted } = element;
    return (
      <div onClick={() => handleGetSingleRide(_id)} className={`sr-listing__card${isCompleted ? " sr-listing__card--completed" : ""}`} key={_id}>
        <div className="sr-listing__card-dots">
          <span className="sr-listing__card-dot sr-listing__card-dot--from" />
          <span className="sr-listing__card-connector" />
          <span className="sr-listing__card-dot sr-listing__card-dot--to" />
        </div>
        <div className="sr-listing__card-route">
          <div className="sr-listing__card-cities">
            <span className="sr-listing__card-city">{departure}</span>
            {route && route !== "nil" && (
              <span className="sr-listing__card-via">
                {route.length > 18 ? `${route.substring(0, 15)}...` : route}
              </span>
            )}
            <span className="sr-listing__card-city">{arrival}</span>
          </div>
        </div>
        <span className="sr-listing__card-date">{formatRideDate(estimatedDate)}</span>
        {isCompleted && (
          <span className="sr-listing__card-badge sr-listing__card-badge--completed">
            <FontAwesomeIcon icon={faCheck} />
            Done
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="sr-listing" ref={containerRef}>
      {/* ── Dark top section ── */}
      <div className="sr-listing__top">
        <div className="sr-listing__top-content">
          <h1 className="sr-listing__title">{isOwnProfile ? "My Rides" : `${profileName}'s Rides`}</h1>
          <p className="sr-listing__subtitle">{isOwnProfile ? "Your offered and booked rides" : "Offered and booked rides"}</p>
        </div>
      </div>

      {/* ── White body section ── */}
      <div className="sr-listing__body">
        {/* Toggle */}
        <div className="sr-listing__toggle" data-tour="listing-toggle">
          <input type="radio" name="slide" id="offer" checked={!isBookingMode} onChange={handleOfferedClick} />
          <input type="radio" name="slide" id="booking" checked={isBookingMode} onChange={handleBookingClick} />
          <label htmlFor="offer" className={`sr-listing__toggle-label ${!isBookingMode ? 'sr-listing__toggle-label--active' : ''}`} onClick={handleOfferedClick}>Offered</label>
          <label htmlFor="booking" className={`sr-listing__toggle-label ${isBookingMode ? 'sr-listing__toggle-label--active' : ''}`} onClick={handleBookingClick}>Booked</label>
          <div className={`sr-listing__toggle-slider ${isBookingMode ? 'sr-listing__toggle-slider--right' : ''}`} />
        </div>

        {(pullDistance > 0 || isRefreshing) && (
          <div className="sr-listing__pull-indicator" style={{ height: pullDistance }}>
            {isRefreshing ? "Refreshing..." : "Pull to refresh"}
          </div>
        )}

        <div className="sr-listing__rides" data-tour="listing-rides">
          {!isBookingMode && (
            <>
              {isOwnProfile && recurringRides.length > 0 && (
                <div className="sr-listing__recurring">
                  <div className="sr-listing__recurring-title">Recurring rides</div>
                  {recurringRides.map((r) => (
                    <div className={`sr-listing__recurring-card${r.isActive ? "" : " sr-listing__recurring-card--paused"}`} key={r._id}>
                      <div className="sr-listing__recurring-info">
                        <div className="sr-listing__recurring-route">
                          <span className="sr-listing__card-city">{r.departure}</span>
                          <span className="sr-listing__recurring-arrow">→</span>
                          <span className="sr-listing__card-city">{r.arrival}</span>
                        </div>
                        <div className="sr-listing__recurring-schedule">
                          {describeSchedule(r.schedule)}
                          {!r.isActive && <span className="sr-listing__recurring-paused-tag">Paused</span>}
                        </div>
                      </div>
                      <div className="sr-listing__recurring-actions">
                        <button
                          type="button"
                          className="sr-listing__recurring-btn"
                          onClick={() => handleToggleRecurring(r._id)}
                        >
                          {r.isActive ? "Pause" : "Resume"}
                        </button>
                        <button
                          type="button"
                          className="sr-listing__recurring-btn sr-listing__recurring-btn--danger"
                          onClick={() => handleDeleteRecurring(r._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {offeredRideError && <div className="sr-listing__empty">{offeredRideError}</div>}
              {offeredRides.map((element) => renderRideCard(element))}
            </>
          )}
          {isBookingMode && (
            <>
              {bookedRideError && <div className="sr-listing__empty">{bookedRideError}</div>}
              {bookedRides.map((element) => renderRideCard(element))}
            </>
          )}
        </div>
      </div>
      <TourButton onClick={startTour} />
    </div>
  );
};

export default Listing;